import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
/**
 * The bar shown while messages are selected: quote into a discussion, copy, move.
 *
 * Rendering the transcript is the server's job — TranscriptController re-checks
 * every id against `whereVisibleTo`, so a selection that names a message the actor
 * cannot read comes back without it, and the count of what was dropped is reported
 * rather than silently swallowed.
 */
export default class ChatSelectionBar extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "working", false);
    _defineProperty(this, "moveTarget", "");
  }
  view() {
    const state = this.attrs.state;
    const count = state.selected.size;
    return m("div", {
      className: "ChatSelectionBar"
    }, m("span", {
      className: "ChatSelectionBar-count"
    }, app.translator.trans("ramon-chat.forum.selection.count", {
      count
    })), m("div", {
      className: "ChatSelectionBar-actions"
    }, m(Button, {
      className: "Button Button--text",
      icon: "fas fa-quote-left",
      disabled: count === 0 || this.working,
      onclick: () => this.quote()
    }, app.translator.trans("ramon-chat.forum.message.quote_in_discussion")), m(Button, {
      className: "Button Button--text",
      icon: "fas fa-copy",
      disabled: count === 0 || this.working,
      onclick: () => this.copy()
    }, app.translator.trans("ramon-chat.forum.message.copy_text")), this.moveControls(count), m(Button, {
      className: "Button Button--text",
      icon: "fas fa-xmark",
      onclick: () => this.cancel()
    }, app.translator.trans("ramon-chat.forum.selection.cancel"))));
  }

  /**
   * Moving needs `ramon-chat.moderate`, and the destination has to be somewhere the
   * actor may post — a closed or archived channel is refused server-side, so those
   * are not offered.
   */
  moveControls(count) {
    const _this$attrs = this.attrs,
      channel = _this$attrs.channel,
      state = _this$attrs.state;
    if (!app.forum.attribute("canModerateChat")) return null;
    const targets = state.channels.filter(candidate => candidate.id() !== channel.id() && candidate.canPostMessage() && !candidate.archivedAt());
    if (targets.length === 0) return null;
    return m('[', null, m("select", {
      className: "FormControl ChatSelectionBar-target",
      value: this.moveTarget,
      onchange: e => {
        this.moveTarget = e.target.value;
      }
    }, m("option", {
      value: ""
    }, app.translator.trans("ramon-chat.forum.selection.move_to", {}, true)), targets.map(target => m("option", {
      key: target.id(),
      value: String(target.id())
    }, target.displayName()))), m(Button, {
      className: "Button Button--text",
      icon: "fas fa-right-left",
      disabled: count === 0 || this.working || this.moveTarget === "",
      onclick: () => this.move()
    }, app.translator.trans("ramon-chat.forum.message.move")));
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  async transcript(format) {
    this.working = true;
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/transcript"),
        body: {
          data: {
            attributes: {
              messageIds: [...this.attrs.state.selected],
              format
            }
          }
        }
      });
      const attributes = payload.data.attributes;
      if (attributes.omitted > 0) {
        app.alerts.show({
          type: "warning"
        }, app.translator.trans("ramon-chat.forum.selection.omitted", {
          count: attributes.omitted
        }));
      }
      return attributes;
    } catch (e) {
      var _e$response$errors$0$, _e$response;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$ = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) != null ? _e$response$errors$0$ : app.translator.trans("ramon-chat.forum.selection.failed"));
      return null;
    } finally {
      this.working = false;
      m.redraw();
    }
  }
  async quote() {
    const rendered = await this.transcript("markup");
    if (!rendered) return;
    const DiscussionComposer = (await flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer')).default;

    // Seeded through `originalContent`, not by writing `fields.content` after
    // load(). ComposerBody.oninit runs when show() mounts the body, and its first
    // act is `fields.content(attrs.originalContent || '')` — so anything written
    // beforehand is overwritten with the empty string, which is why the composer
    // opened blank. Passing it as an attr is the same route EditPostComposer takes
    // to pre-fill a post's text, and it also means closing straight away does not
    // ask to discard: nothing has been typed yet.
    await app.composer.load(() => Promise.resolve({
      default: DiscussionComposer
    }), {
      user: app.session.user,
      originalContent: rendered.content
    });
    await app.composer.show();
    this.cancel();
  }
  async copy() {
    const rendered = await this.transcript("plain");
    if (!rendered) return;
    try {
      await navigator.clipboard.writeText(rendered.content);
      app.alerts.show({
        type: "success"
      }, app.translator.trans("ramon-chat.forum.selection.copied"));
      this.cancel();
    } catch (_unused) {
      // Clipboard access is refused outside a secure context and in some
      // embedded browsers. Say so rather than appearing to have copied.
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.selection.copy_failed"));
    }
  }
  async move() {
    const _this$attrs2 = this.attrs,
      state = _this$attrs2.state,
      channel = _this$attrs2.channel;
    const ids = [...state.selected];
    this.working = true;
    m.redraw();
    try {
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/messages/move"),
        body: {
          data: {
            attributes: {
              messageIds: ids,
              channelId: Number(this.moveTarget)
            }
          }
        }
      });

      // The messages are no longer in this channel; drop them from the window
      // rather than refetching the whole page.
      for (const id of ids) {
        state.removeMessage(Number(channel.id()), id);
      }
      app.alerts.show({
        type: "success"
      }, app.translator.trans("ramon-chat.forum.selection.moved", {
        count: ids.length
      }));
      this.cancel();
    } catch (e) {
      var _e$response$errors$0$2, _e$response2;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$2 = e == null || (_e$response2 = e.response) == null || (_e$response2 = _e$response2.errors) == null || (_e$response2 = _e$response2[0]) == null ? void 0 : _e$response2.detail) != null ? _e$response$errors$0$2 : app.translator.trans("ramon-chat.forum.selection.failed"));
    } finally {
      this.working = false;
      m.redraw();
    }
  }
  cancel() {
    const state = this.attrs.state;
    state.selecting = false;
    state.selected.clear();
    this.moveTarget = "";
    m.redraw();
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChatSelectionBar', ChatSelectionBar);